import * as React from "react";

import { ActionIcon, Group, Popover, Stack, Text, Tooltip } from "@mantine/core";
import { IconClearFormatting, IconCopy, IconSettings } from "@tabler/icons-react";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useDocumentStyles } from "../../hooks/useDocumentStyles";
import {
  ChecklistElement,
  isChecklistHeader,
  isChecklistItem,
} from "../../lib/model/checklistAppState";
import { ChecklistDocumentStyles } from "../../lib/model/documentStyles";
import { librarySlice } from "../../lib/state/reducer";

import { CompactColorPicker } from "./CompactColorPicker";
import { FontWeight, FontWeightSelector, isFontWeight } from "./FontWeightSelector";

function computeDefaultWeight(el: ChecklistElement, styles: ChecklistDocumentStyles): FontWeight {
  if (isChecklistHeader(el)) {
    if (styles.checklistHeader.fontWeight && isFontWeight(styles.checklistHeader.fontWeight)) {
      return styles.checklistHeader.fontWeight;
    } else {
      return 400;
    }
  }
  if (isChecklistItem(el)) {
    if (styles.checklistItem.fontWeight && isFontWeight(styles.checklistItem.fontWeight)) {
      return styles.checklistItem.fontWeight;
    } else {
      return 400;
    }
  }
  const _exhaustiveCheck: never = el;
  return _exhaustiveCheck;
}

// TODO: make the font bolding dynamic based on what font weights are supplied for the font
export const ChecklistElementAdjustmentsMenu = ({
  checklistElement,
}: {
  checklistElement: ChecklistElement;
}): JSX.Element => {
  const [popoverOpen, setPopoverOpen] = React.useState<boolean>(false);
  const reduxDispatch = useAppDispatch();
  const styles = useDocumentStyles();

  return (
    <Popover
      opened={popoverOpen}
      onChange={setPopoverOpen}
      trapFocus
      position="top-end"
      width={225}
      withArrow
      shadow="xl"
      offset={0}
      styles={(theme) => ({
        dropdown: {
          border: `1px solid ${theme.colors.gray[4]}`,
          color: "revert",
        },
        arrow: { border: `1px solid ${theme.colors.gray[4]}` },
      })}
    >
      <Popover.Target>
        <Tooltip withArrow label="Adjustments" openDelay={500} disabled={popoverOpen}>
          <ActionIcon size="sm" onClick={() => setPopoverOpen(!popoverOpen)}>
            <IconSettings size={16} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p={"xs"}>
        <Stack spacing="xs">
          <Group position="apart" spacing={"xs"}>
            <Text fz="sm">Background Color</Text>
            <CompactColorPicker
              value={checklistElement.styles?.backgroundColor ?? "#fff"}
              defaultSwatchColorIndex={1}
              onChange={(newValue) => {
                reduxDispatch(
                  librarySlice.actions.updateChecklistElementStyles({
                    elementId: checklistElement.id,
                    styles: { backgroundColor: newValue },
                  })
                );
              }}
            />
          </Group>
          <Group position="apart" spacing={"xs"}>
            <Text fz="sm">Text Color</Text>
            <CompactColorPicker
              value={checklistElement.styles?.color ?? "#000"}
              defaultSwatchColorIndex={9}
              onChange={(newValue) => {
                reduxDispatch(
                  librarySlice.actions.updateChecklistElementStyles({
                    elementId: checklistElement.id,
                    styles: { color: newValue },
                  })
                );
              }}
            />
          </Group>
          <Group position="apart" spacing={"xs"}>
            <Text fz="sm">Font Weight</Text>
            <FontWeightSelector
              selected={checklistElement.styles?.fontWeight}
              defaultWeight={computeDefaultWeight(checklistElement, styles)}
              onClear={() => {
                const styleCopy = { ...checklistElement.styles };
                delete styleCopy.fontWeight;
                reduxDispatch(
                  librarySlice.actions.updateChecklistElementStyles({
                    elementId: checklistElement.id,
                    styles: null,
                  })
                );
                reduxDispatch(
                  librarySlice.actions.updateChecklistElementStyles({
                    elementId: checklistElement.id,
                    styles: styleCopy,
                  })
                );
              }}
              onSelect={(newWeight) => {
                reduxDispatch(
                  librarySlice.actions.updateChecklistElementStyles({
                    elementId: checklistElement.id,
                    styles: { fontWeight: newWeight },
                  })
                );
              }}
            />
          </Group>
          <Group spacing="xs" position="right">
            <Tooltip withArrow label="Duplicate">
              <ActionIcon
                size="sm"
                variant="filled"
                color="dark"
                sx={(theme) => ({ backgroundColor: theme.colors.dark[4] })}
                onClick={() => {
                  reduxDispatch(librarySlice.actions.duplicateChecklistElement(checklistElement));
                }}
              >
                <IconCopy size={14} />
              </ActionIcon>
            </Tooltip>
            <Tooltip withArrow label="Clear Formatting">
              <ActionIcon
                size="sm"
                variant="filled"
                color="dark"
                sx={(theme) => ({ backgroundColor: theme.colors.dark[4] })}
                onClick={() => {
                  reduxDispatch(
                    librarySlice.actions.updateChecklistElementStyles({
                      elementId: checklistElement.id,
                      styles: null,
                    })
                  );
                }}
              >
                <IconClearFormatting size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
